import { useEffect, useRef, useState } from "react";
import { ArrowRight } from "lucide-react";
import useIsMobile from "@/hooks/use-is-mobile";
import { APP_REGISTER_URL, APP_LINK_PROPS, track } from "@/lib/config";

// Hauteur réservée à la barre (bouton + paddings) exposée aux autres éléments
// flottants (bulle du chat) via une variable CSS pour éviter tout chevauchement.
const BAR_HEIGHT = "4.75rem";

/**
 * Barre CTA fixe, mobile uniquement.
 * - Ne rend rien tant qu'elle n'est pas active : aucun encart vide / bande floutée
 *   peinte hors écran au démarrage.
 * - Apparaît (fade-in) une fois le hero dépassé, se masque quand la section
 *   de conversion "#devis" est visible (le CTA y est déjà présent).
 * - Expose sa hauteur via `--mobile-cta-h` pour décaler la bulle du chat.
 */
export default function MobileCtaBar() {
    const isMobile = useIsMobile();
    const [show, setShow] = useState(false);
    const pastHero = useRef(false);
    const formVisible = useRef(false);

    useEffect(() => {
        if (!isMobile || typeof window === "undefined" || !("IntersectionObserver" in window)) return;

        const root = document.documentElement;
        const hero = document.querySelector("section.isolate") || document.querySelector("main section");
        const conversion = document.getElementById("devis");

        const sync = () => {
            const next = pastHero.current && !formVisible.current;
            setShow(next);
            root.style.setProperty("--mobile-cta-h", next ? BAR_HEIGHT : "0px");
        };

        const observers = [];

        if (hero) {
            const heroObs = new IntersectionObserver(
                ([e]) => {
                    // Visible seulement quand le hero n'est plus à l'écran.
                    pastHero.current = !e.isIntersecting;
                    sync();
                },
                { threshold: 0 }
            );
            heroObs.observe(hero);
            observers.push(heroObs);
        } else {
            pastHero.current = true;
        }

        if (conversion) {
            const formObs = new IntersectionObserver(
                ([e]) => {
                    formVisible.current = e.isIntersecting;
                    sync();
                },
                { threshold: 0.15 }
            );
            formObs.observe(conversion);
            observers.push(formObs);
        }

        sync();
        return () => {
            observers.forEach((o) => o.disconnect());
            root.style.setProperty("--mobile-cta-h", "0px");
        };
    }, [isMobile]);

    if (!isMobile || !show) return null;

    return (
        <div
            data-testid="mobile-cta-bar"
            className="fixed inset-x-0 bottom-0 z-40 animate-fade-in-up border-t border-slate-200 bg-white/95 backdrop-blur-md px-4 py-3 shadow-[0_-8px_30px_-10px_rgba(67,56,202,0.25)]"
            style={{ paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))" }}
        >
            <a
                href={APP_REGISTER_URL}
                {...APP_LINK_PROPS}
                onClick={() => track("cta_clicked", { cta: "mobile_sticky" })}
                className="flex w-full items-center justify-center gap-2 rounded-2xl bg-gradient-to-br from-primary-700 to-primary-600 px-6 py-3.5 text-base font-medium text-white shadow-[0_12px_30px_-12px_rgba(67,56,202,0.6)] active:scale-[0.99] transition-transform"
            >
                Démarrer
                <ArrowRight className="h-5 w-5" />
            </a>
        </div>
    );
}
